import React, { useMemo } from 'react';
import { Info } from 'lucide-react';
import { Tooltip, TooltipContent, TooltipTrigger } from './ui/tooltip';
import { healthFromKpiArray, HEALTH_BAND_BG, HEALTH_BAND_RING } from '../lib/health';

/** Priority index for sorting KPIs worst-first. */
const SEMAPHORE_PRIORITY = { red: 0, yellow: 1, green: 2, neutral: 3, undefined: 3, null: 3 };

const TYPE_LABELS = {
  revenue: 'Ricavi',
  efficiency: 'Efficienza',
  client: 'Clienti',
  quality: 'Qualità',
  count: 'Volumi',
  capacity: 'Capacità',
};

const SEMAPHORE_BADGE = HEALTH_BAND_BG;

function fmt(val, format) {
  if (val == null) return '—';
  if (format === 'currency') return new Intl.NumberFormat('it-IT', { style: 'currency', currency: 'EUR' }).format(val);
  if (format === 'percentage') return `${val.toFixed(1)}%`;
  if (format === 'hours') return `${val.toFixed(1)}h`;
  if (format === 'integer') return Math.round(val).toLocaleString('it-IT');
  return val.toLocaleString('it-IT', { maximumFractionDigits: 2 });
}

function fmtVar(val, format) {
  if (val == null) return '—';
  const sign = val > 0 ? '+' : '';
  if (format === 'percentage' || format === 'hours') return `${sign}${val.toFixed(1)}${format === 'percentage' ? 'pp' : 'h'}`;
  if (format === 'currency') return `${sign}${fmt(val, 'currency')}`;
  if (format === 'integer') return `${sign}${Math.round(val).toLocaleString('it-IT')}`;
  return `${sign}${val.toLocaleString('it-IT', { maximumFractionDigits: 1 })}`;
}

function useHealthScore(kpis) {
  return useMemo(() => healthFromKpiArray(kpis), [kpis]);
}

/** Sort KPIs red → yellow → green → neutral, stable secondary by original order. */
function useSortedByCriticality(kpis) {
  return useMemo(() => {
    const indexed = (kpis || []).map((k, i) => ({ ...k, _idx: i }));
    indexed.sort((a, b) => {
      const pa = SEMAPHORE_PRIORITY[a.sem_budget] ?? 3;
      const pb = SEMAPHORE_PRIORITY[b.sem_budget] ?? 3;
      if (pa !== pb) return pa - pb;
      return a._idx - b._idx;
    });
    return indexed;
  }, [kpis]);
}

function ScoreRing({ score, band, size = 72 }) {
  if (score == null) {
    return (
      <div
        className="flex items-center justify-center border border-slate-200 bg-slate-50 text-slate-400 text-xs"
        style={{ width: size, height: size }}
        data-testid="health-score-empty"
      >
        —
      </div>
    );
  }
  const stroke = 6;
  const r = (size - stroke) / 2;
  const c = 2 * Math.PI * r;
  const dash = (score / 100) * c;
  return (
    <div className="relative" style={{ width: size, height: size }} data-testid="health-score-ring">
      <svg width={size} height={size}>
        <circle
          cx={size / 2} cy={size / 2} r={r}
          fill="none" stroke="#e2e8f0" strokeWidth={stroke}
        />
        <circle
          cx={size / 2} cy={size / 2} r={r}
          fill="none" strokeWidth={stroke}
          strokeLinecap="round"
          strokeDasharray={`${dash} ${c}`}
          className={`${HEALTH_BAND_RING[band]} transition-all`}
          transform={`rotate(-90 ${size / 2} ${size / 2})`}
        />
      </svg>
      <div className="absolute inset-0 flex flex-col items-center justify-center">
        <span className="font-heading text-lg md:text-xl font-bold tabular-nums leading-none">{score}</span>
        <span className="text-[9px] uppercase tracking-wider text-slate-400">su 100</span>
      </div>
    </div>
  );
}

export function HealthScoreHeader({ kpis, periodLabel }) {
  const { score, band, counts, sample } = useHealthScore(kpis);
  return (
    <div
      className="bg-white border border-slate-200 px-4 md:px-5 py-4 flex items-center gap-4 md:gap-6"
      data-testid="store-health-score"
    >
      <ScoreRing score={score} band={band} />
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-2">
          <p className="text-[10px] uppercase tracking-[0.18em] font-semibold text-slate-500">Health score</p>
          <Tooltip>
            <TooltipTrigger asChild>
              <button type="button" aria-label="Come viene calcolato" className="text-slate-400 hover:text-slate-700">
                <Info className="w-3 h-3" />
              </button>
            </TooltipTrigger>
            <TooltipContent side="top" className="max-w-xs text-xs leading-relaxed">
              <p className="font-semibold text-sm mb-1.5">Come si calcola</p>
              <p>
                Media pesata dei semafori budget dei KPI: i <b>KPI principali</b> (vendite, ticket
                medio, ricavo orario, occupazione) pesano il doppio rispetto a quelli secondari.
                Ogni KPI vale verde 100, giallo 80, rosso 45.
              </p>
              <ul className="mt-2 space-y-0.5">
                <li><b className="text-emerald-700">Verde</b> · &gt; 75 (o 70-75 con più verdi che rossi)</li>
                <li><b className="text-amber-700">Giallo</b> · 60-75 (zona attenzione)</li>
                <li><b className="text-red-700">Rosso</b> · &lt; 60</li>
              </ul>
              <p className="mt-2 text-[10px] text-slate-500">Esclusi: Ore Disponibili, Clienti Abituali.</p>
            </TooltipContent>
          </Tooltip>
        </div>
        <p className="font-heading text-base md:text-lg font-semibold mt-1 leading-tight">
          {score == null
            ? 'Non abbastanza dati di budget per questo periodo'
            : band === 'green' ? 'Andamento buono'
            : band === 'yellow' ? 'In linea con attenzione'
            : 'Sotto target — intervento necessario'}
        </p>
        <div className="flex items-center gap-3 mt-1.5 text-xs text-slate-500 flex-wrap" data-testid="health-score-breakdown">
          {score != null && (
            <>
              <span className="inline-flex items-center gap-1">
                <span className="w-2 h-2 bg-emerald-500" /> {counts.green} KPI verdi
              </span>
              <span className="inline-flex items-center gap-1">
                <span className="w-2 h-2 bg-amber-500" /> {counts.yellow} gialli
              </span>
              <span className="inline-flex items-center gap-1">
                <span className="w-2 h-2 bg-red-500" /> {counts.red} rossi
              </span>
              <span className="text-slate-300">·</span>
              <span>{periodLabel}</span>
            </>
          )}
          {score == null && <span>{periodLabel}</span>}
          {score != null && <span className="text-slate-300 hidden md:inline">({sample} KPI valutati)</span>}
        </div>
      </div>
    </div>
  );
}

/** Mobile card list, sorted worst-first. */
function KpiCardsMobile({ kpis, sorted }) {
  if (!kpis || kpis.length === 0) {
    return <p className="text-sm text-slate-400 italic p-5">Nessun KPI per il periodo selezionato.</p>;
  }
  return (
    <ul className="divide-y divide-slate-100" data-testid="kpi-cards-mobile">
      {sorted.map(k => {
        const sem = k.sem_budget || 'neutral';
        const hasBudget = k.budget != null && k.var_budget != null;
        const varPct = hasBudget && k.budget !== 0 ? (k.var_budget / k.budget) * 100 : null;
        return (
          <li key={k.key} className="px-4 py-3.5" data-testid={`kpi-card-${k.key}`}>
            <div className="flex items-baseline justify-between gap-3">
              <div className="min-w-0 flex-1">
                <p className="text-[10px] uppercase tracking-wider font-semibold text-slate-400">
                  {TYPE_LABELS[k.type] || k.type}
                </p>
                <p className="font-semibold text-sm text-slate-900 mt-0.5 truncate">{k.label}</p>
              </div>
              <span
                className={`inline-flex items-center px-2 py-0.5 text-[11px] font-semibold uppercase tracking-wider border ${SEMAPHORE_BADGE[sem]}`}
                data-testid={`kpi-card-${k.key}-badge`}
              >
                {sem === 'green' ? 'in linea' : sem === 'yellow' ? 'attenzione' : sem === 'red' ? 'critico' : '—'}
              </span>
            </div>
            <div className="flex items-end justify-between gap-3 mt-2">
              <div>
                <p className="font-heading text-2xl font-bold tabular-nums leading-none" data-testid={`kpi-card-${k.key}-actual`}>
                  {fmt(k.actual, k.format)}
                </p>
                <p className="text-[11px] text-slate-500 mt-1 tabular-nums">
                  Budget {fmt(k.budget, k.format)}
                  {k.last_year != null && <span> · A.P. {fmt(k.last_year, k.format)}</span>}
                </p>
              </div>
              <div className="text-right">
                {hasBudget ? (
                  <>
                    <p className={`text-sm font-bold tabular-nums ${sem === 'red' ? 'text-red-700' : sem === 'green' ? 'text-emerald-700' : sem === 'yellow' ? 'text-amber-700' : 'text-slate-500'}`} data-testid={`kpi-card-${k.key}-delta`}>
                      {fmtVar(k.var_budget, k.format)}
                    </p>
                    {varPct != null && (
                      <p className="text-[10px] text-slate-400 tabular-nums">
                        {varPct > 0 ? '+' : ''}{varPct.toFixed(1)}% vs budget
                      </p>
                    )}
                  </>
                ) : (
                  <p className="text-xs text-slate-400 italic">no budget</p>
                )}
              </div>
            </div>
            {hasBudget && k.budget > 0 && (
              <div className="mt-2.5 h-1 bg-slate-100 overflow-hidden" data-testid={`kpi-card-${k.key}-bar`}>
                <div
                  className={`h-full ${sem === 'green' ? 'bg-emerald-500' : sem === 'yellow' ? 'bg-amber-500' : sem === 'red' ? 'bg-red-500' : 'bg-slate-300'}`}
                  style={{ width: `${Math.min(100, Math.max(0, ((k.actual || 0) / k.budget) * 100))}%` }}
                />
              </div>
            )}
          </li>
        );
      })}
    </ul>
  );
}

/** Public API: renders health score + KPI table (desktop) / prioritised cards (mobile).
 *
 * The caller is expected to pass the `kpis` array from `dashboardApi.store()`.
 * Desktop layout (`md:` breakpoint and up) stays the classic multi-column
 * accounting table — it's fast to scan once you know the shape. Mobile
 * layout gets a ground-up redesign: cards sorted by criticality, each with
 * a clear actual value, a semaphore badge, a one-line reference for
 * budget / last year, and a bar showing progress toward budget.
 *
 * `renderDesktopTable` is passed by the parent so we don't duplicate the
 * (reasonably complex) existing table markup — we just wrap it in a
 * "hidden on mobile" shell and provide the sorted mobile alternative.
 */
export default function StoreKpiPanel({ kpis, periodLabel, renderDesktopTable, hideHealthHeader = false }) {
  const sorted = useSortedByCriticality(kpis);
  return (
    <div className="space-y-4" data-testid="store-kpi-panel">
      {!hideHealthHeader && <HealthScoreHeader kpis={kpis} periodLabel={periodLabel} />}

      {/* Desktop: il parent fornisce la tabella classica — la render qui
          dentro lo shell per non duplicare markup. */}
      <div className="hidden md:block">
        {renderDesktopTable?.()}
      </div>

      {/* Mobile: redesign completo a cards ordinate per criticità. */}
      <div className="md:hidden bg-white border border-slate-200" data-testid="kpi-cards-mobile-wrap">
        <div className="px-4 py-3 border-b border-slate-200 flex items-center justify-between">
          <h2 className="font-heading text-base font-semibold tracking-tight">KPI per criticità</h2>
          <span className="text-[10px] uppercase tracking-wider text-slate-400">ordine: rossi prima</span>
        </div>
        <KpiCardsMobile kpis={kpis} sorted={sorted} />
      </div>
    </div>
  );
}
